(() => {
class OrderSummary extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.order-summary__promo')
    this.input = this.querySelector('.order-summary__promo-input')
    this.submitButton = this.querySelector('.order-summary__promo-button')
    this.status = this.querySelector('.order-summary__promo-status')
    this.codes = this.querySelector('.order-summary__applied-codes')
    this.form?.addEventListener('submit', this.onSubmit.bind(this))
    this.codes?.addEventListener('click', this.onCodeClick.bind(this))
    this.renderCodes()
  }

  async getCart() {
    if (window.Shopify?.actions?.getCart) {
      const result = await window.Shopify.actions.getCart()
      return result.cart
    }
    const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error('Could not load the cart.')
    return response.json()
  }

  async updateCodes(discountCodes) {
    if (window.Shopify?.actions?.updateCart) {
      return window.Shopify.actions.updateCart({ discountCodes })
    }
    const response = await fetch(this.dataset.cartUpdateUrl, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: discountCodes.join(',') }),
    })
    if (!response.ok) throw new Error('Could not update the cart.')
    const cart = await response.json()
    return { cart }
  }

  async refreshSummarySurfaces() {
    const footer = document.getElementById('main-cart-footer')
    const drawerFooter = document.querySelector('.cart-drawer__footer')
    const footerSectionId = footer?.dataset.id
    const sectionIds = [...new Set([footerSectionId, drawerFooter ? 'cart-drawer' : null].filter(Boolean))]

    if (!sectionIds.length) throw new Error('No order summary sections are available to refresh.')

    const sectionsUrl = new URL(this.dataset.cartUrl, window.location.origin)
    sectionsUrl.searchParams.set('sections', sectionIds.join(','))
    const response = await fetch(sectionsUrl, { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error('Could not refresh the order summary.')

    const sections = await response.json()
    const parser = new DOMParser()
    let replaced = false

    if (footer && footerSectionId) {
      const html = sections[footerSectionId]
      const replacement = html
        ? parser.parseFromString(html, 'text/html').querySelector('#main-cart-footer .js-contents')
        : null
      const current = footer.querySelector('.js-contents')
      if (!replacement || !current) throw new Error('Could not parse the cart-page order summary.')
      current.replaceWith(replacement)
      replaced = true
    }

    if (drawerFooter) {
      const html = sections['cart-drawer']
      const replacement = html
        ? parser.parseFromString(html, 'text/html').querySelector('.cart-drawer__footer')
        : null
      const current = document.querySelector('.cart-drawer__footer')
      if (!replacement || !current) throw new Error('Could not parse the drawer order summary.')
      current.replaceWith(replacement)
      replaced = true
    }

    if (!replaced) throw new Error('No order summary was refreshed.')
  }

  async refreshAfterMutation(message, isError = false) {
    try {
      await this.refreshSummarySurfaces()
      this.setStatus(message, isError)
    } catch (_) {
      window.location.reload()
    }
  }

  async onSubmit(event) {
    event.preventDefault()
    const code = this.input?.value.trim()
    if (!code) return this.setStatus('Enter a promo code.', true)
    this.setLoading(true)
    try {
      const cart = await this.getCart()
      const existing = this.getApplicableCodes(cart)
      const codes = [...new Set([...existing, code])]
      const result = await this.updateCodes(codes)
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message)
      const updatedCart = result?.cart || await this.getCart()
      const applied = (updatedCart?.discountCodes || updatedCart?.discount_codes || []).find((item) => (typeof item === 'string' ? item : item.code).toLowerCase() === code.toLowerCase())
      if (!applied || (typeof applied !== 'string' && applied.applicable === false)) {
        await this.updateCodes(existing)
        await this.refreshAfterMutation('This promo code cannot be applied to your cart.', true)
        return
      }
      this.input.value = ''
      await this.refreshAfterMutation('Promo code applied.')
    } catch (error) {
      this.setStatus('We could not apply that promo code. Please try again.', true)
    } finally {
      this.setLoading(false)
    }
  }

  async onCodeClick(event) {
    const button = event.target.closest('[data-discount-code]')
    if (!button) return
    this.setLoading(true)
    try {
      const cart = await this.getCart()
      const codes = this.getApplicableCodes(cart).filter((code) => code.toLowerCase() !== button.dataset.discountCode.toLowerCase())
      const result = await this.updateCodes(codes)
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message)
      await this.refreshAfterMutation('Promo code removed.')
    } catch (error) {
      this.setStatus('We could not remove that promo code. Please try again.', true)
    } finally {
      this.setLoading(false)
    }
  }

  async renderCodes(cart) {
    if (!this.codes) return
    try {
      const currentCart = cart || await this.getCart()
      const codes = currentCart?.discountCodes || currentCart?.discount_codes || []
      this.codes.replaceChildren(...codes.filter((item) => item.applicable !== false).map((item) => {
        const code = typeof item === 'string' ? item : item.code
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'order-summary__applied-code'
        button.dataset.discountCode = code
        button.textContent = `${code} \u00d7`
        button.setAttribute('aria-label', `Remove promo code ${code}`)
        return button
      }))
    } catch (_) {}
  }

  setLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading
      this.submitButton.setAttribute('aria-busy', String(loading))
    }
    if (this.input) this.input.disabled = loading
  }

  getApplicableCodes(cart) {
    return (cart?.discountCodes || cart?.discount_codes || [])
      .filter((item) => typeof item === 'string' || item.applicable !== false)
      .map((item) => typeof item === 'string' ? item : item.code)
      .filter(Boolean)
  }

  setStatus(message, isError = false) {
    const statuses = document.querySelectorAll('.order-summary__promo-status')
    statuses.forEach((status) => {
      status.textContent = message
      status.classList.toggle('order-summary__promo-status--error', isError)
    })
  }
}

if (!customElements.get('order-summary')) customElements.define('order-summary', OrderSummary)
})()
